import React, {useEffect, useState, useRef} from "react";
import {
    IonButton, IonCol,
    IonContent, IonGrid,
    IonHeader,
    IonPage, IonRow,
    IonSelect,
    IonSelectOption,
    IonText,
    IonTitle, IonToggle,
    IonToolbar
} from "@ionic/react";
import {AcaGetOpportunityResponseOpportunity, ContactType} from "../types";

type ProviderStateRec = { state: string; name: string };

interface Props {
    providerStates: ProviderStateRec[];
    opportunity: AcaGetOpportunityResponseOpportunity;
    contact: ContactType;
    dismiss: (
        data?: {
            carrierProvider?: string | null;
            carrierState?: string | null;
            carrierMemberCount?: number | null;
            dataOnly?: boolean;
        } | null,
        role?: string
    ) => void;
}

const ModalEditOpportunity: React.FC<Props> = ({providerStates, opportunity, contact, dismiss}) => {

    //local state w current values
    const [carrierState, setCarrierState] = useState<string | null>(opportunity.carrierState);
    const [carrierProvider, setCarrierProvider] = useState<string | null>(opportunity.carrierProvider);
    const [carrierMemberCount, setCarrierMemberCount] = useState<number | null>(opportunity.carrierMemberCount);

    const [availableStates, setAvailableStates] = useState<string[]>([]);
    const [availableProviders, setAvailableProviders] = useState<string[]>([]);
    const [isValid, setIsValid] = useState<boolean>(false);
    const [allowedState, setAllowedState] = useState<string | null>(contact.addressState);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [dataOnly, setDataOnly] = useState<boolean>(opportunity.dataOnly);

    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        setAllowedState(allowedState);
    }, []);

    useEffect(() => {
        if (!carrierState || !carrierProvider) {
            setIsValid(false);
            setErrorMsg('You must select a carrier state and carrier provider to continue.');
            return;
        }
        if (allowedState && carrierState !== allowedState) {
            setErrorMsg(
                `Carrier State does not match the contact’s address state (${allowedState}). Please verify this is correct before continuing.`
            );
        } else {
            setErrorMsg(null);
        }
        setIsValid(true);
    }, [carrierState, carrierProvider, allowedState]);

    //build state list
    useEffect(() => {
        const uniq = [...new Set(providerStates.map(r => r.state))].sort((a, b) => a.localeCompare(b));
        setAvailableStates(uniq);
    }, [providerStates]);

    //filter providers on state change
    useEffect(() => {
        if (!carrierState) {
            setAvailableProviders([]);
            setCarrierProvider(null);
            return;
        }
        const prov = providerStates
            .filter(r => r.state === carrierState)
            .map(r => r.name).sort((a, b) => a.localeCompare(b));
        setAvailableProviders(prov);
        //clear provider if not on list
        if (carrierProvider && !prov.includes(carrierProvider)) {
            setCarrierProvider(null);
        }
    }, [carrierState, providerStates]);

    return (
        <IonPage ref={modal}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Edit Enrollment Info</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">

                <IonGrid>
                    <IonRow class="ion-align-items-center">
                        <IonCol>
                            <b>Carrier State:</b>
                        </IonCol>
                        <IonCol>
                            <IonSelect label="Update Carrier State" labelPlacement="floating"  fill="outline" value={carrierState} onIonChange={e=>setCarrierState(e.detail.value)}>
                                {availableStates.map(s=>(
                                    <IonSelectOption key={s} value={s}>{s}</IonSelectOption>
                                ))}
                            </IonSelect>
                        </IonCol>
                    </IonRow>
                    <IonRow class="ion-align-items-center">
                        <IonCol>
                            <b>Carrier Provider:</b>
                        </IonCol>
                        <IonCol>
                            <IonSelect label="Update Carrier Provider" labelPlacement="floating" fill="outline" value={carrierProvider} onIonChange={e=>setCarrierProvider(e.detail.value)}>
                                {availableProviders.map(p=>(
                                    <IonSelectOption key={p} value={p}>{p}</IonSelectOption>
                                ))}
                            </IonSelect>
                        </IonCol>
                    </IonRow>
                    <IonRow class="ion-align-items-center">
                        <IonCol>
                            <b>Carrier Member Count:</b>
                        </IonCol>
                        <IonCol>
                            <IonSelect label="Update Num of Members" labelPlacement="floating"  fill="outline" value={carrierMemberCount ?? 1}
                                       onIonChange={e=>setCarrierMemberCount(Number(e.detail.value))}>
                                {[1,2,3,4,5,6,7,8].map(n=>(
                                    <IonSelectOption key={n} value={n}>{n}</IonSelectOption>
                                ))}
                            </IonSelect>
                        </IonCol>
                    </IonRow>
                    <IonRow class="ion-align-items-center">
                        <IonCol><b>Enrolled-No Call:</b></IonCol>
                        <IonCol>
                            <IonToggle checked={dataOnly} onIonChange={e => setDataOnly(e.detail.checked)}/>
                        </IonCol>
                    </IonRow>
                </IonGrid>

                {errorMsg && (<IonText color="danger"><b style={{ marginBottom: "6px" }}>{errorMsg}</b></IonText>)}
                <p style={{textAlign:'right', marginTop:'25px'}}>
                    <IonButton fill="outline" onClick={()=>dismiss(null,'cancel')}>Cancel</IonButton>
                    <IonButton disabled={!isValid} onClick={()=>dismiss({carrierState, carrierProvider, carrierMemberCount, dataOnly}, 'confirm')}>
                        Save
                    </IonButton>
                </p>

            </IonContent>
        </IonPage>
    );
};

export default ModalEditOpportunity;